import { createQueryKeys } from '@lukemorales/query-key-factory';
import {
  getConversations,
  getConversation,
  getAgents,
  getAgent,
  getAgentStarters,
  getAgentTools,
  getSkill,
  getSkills,
  getInvokableSkills,
  getIntegrationCatalog,
  getIntegrations,
  getIntegration,
  checkIntegrationHealth,
  getKBRegistry,
  getKBTrees,
  getKBDocTypes,
  getKBGroups,
  getAgentKnowledge,
  getAgentAccess,
  getUserPreferences,
  getUserMemory,
  getUserConnections,
  getAgentVersions,
  getDatasets,
  getDatasetVersions,
  getDatasetItems,
  getEvaluation,
  listEvaluations,
  getEvaluationMetrics,
} from '../components/ask-moo/api';
import type { StartersResponse } from '../components/ask-moo/types';
import { getActiveAsyncTasks } from '../components/ask-moo/api/async-tasks.api';
import {
  getCustomSkill,
  getCustomSkills,
} from '../components/ask-moo/api/custom-skills.api';
import {
  listMooTasks,
  MOO_TASKS_MAX_LIMIT,
  getMooTask,
  listTaskRunsForTask,
  listAllTaskRuns,
  listTriggerCatalog,
  listOutputCatalog,
  getTaskRunBackgroundProgress,
} from '../components/ask-moo/api/moo-tasks.api';
import { getUsers } from '../api/users/users.api';
import { getData } from '../utils/functional/fp';
import type {
  MooTask,
  TaskListResponse,
} from '../components/ask-moo/types/moo-tasks.types';

/** Every task matching `params`, walking `/moo-tasks` a full page at a time.
 *  The endpoint returns 50 rows unless asked for more, and the Studio lists
 *  filter by owner and name on the client, so they need the whole set rather
 *  than one server page. */
async function listAllMooTasks(
  params?: Record<string, unknown>
): Promise<TaskListResponse> {
  const tasks: MooTask[] = [];
  let total = 0;
  do {
    const { data } = await listMooTasks({
      params: { ...params, limit: MOO_TASKS_MAX_LIMIT, offset: tasks.length },
    });
    total = data.total;
    tasks.push(...data.tasks);
    if (data.tasks.length === 0) break;
  } while (tasks.length < total);
  // Offsets shift if a task is created or reordered mid-walk, which can
  // repeat a row across pages; keep the first copy of each id.
  const unique = Array.from(new Map(tasks.map((t) => [t.id, t])).values());
  return {
    tasks: unique,
    total: unique.length,
    limit: unique.length,
    offset: 0,
  };
}

export const askMoo = createQueryKeys('askMoo', {
  conversations: (params?: {
    cursor?: string;
    limit?: number;
    agentId?: string;
  }) => ({
    queryKey: [{ params }],
    queryFn: () => getConversations({ params }).then(getData),
  }),

  conversation: (conversationId: string) => ({
    queryKey: [{ conversationId }],
    queryFn: () => getConversation(conversationId).then(getData),
  }),

  // Scoped by the account type the user is acting in: the backend filters the
  // agent catalog on the JWT account type, so an in-app account switch (e.g. a
  // Cowbell user entering a CUSTOMER account) must be a cache miss, not a stale
  // hit — otherwise the restricted-persona clamp and the agent picker keep
  // showing the previous account's catalog. `_def` invalidation still matches
  // every scope.
  // `scope` mirrors the backend's custom-agent visibility scope (OMNI-796):
  // omitted/"mine" is the default owner-or-shared catalog every role gets;
  // "all" is the Super-Admin-only full-tenant view for the All Custom Agents
  // tab. It's a separate cache entry (and separate GET) from the default
  // scope, not a client-side filter over it.
  agents: (accountScope?: string, scope?: string) => ({
    queryKey: [{ accountScope: accountScope ?? null, scope: scope ?? null }],
    queryFn: () =>
      (scope ? getAgents({ params: { scope } }) : getAgents())
        .then(getData)
        .then((response) => response.agents),
  }),

  agent: (agentId: string) => ({
    queryKey: [{ agentId }],
    queryFn: () => getAgent(agentId).then(getData),
  }),

  skills: () => ({
    queryKey: [{}],
    queryFn: () =>
      getSkills()
        .then(getData)
        .then((response) => response.skills),
  }),

  agentCommands: (agentId: string) => ({
    queryKey: [{ agentId, invokable: true }],
    queryFn: () =>
      getInvokableSkills({ params: { agent_id: agentId, invokable: true } })
        .then(getData)
        .then((response) => response.skills),
  }),

  skill: (skillId: string) => ({
    queryKey: [{ skillId }],
    queryFn: () => getSkill(skillId).then(getData),
  }),

  // NOTE: the `retry: false` below is inert and kept only as documentation
  // of intent. `createQueryKeys` forwards nothing but `queryKey`/`queryFn`/
  // `contextQueries` out of a factory entry's return value, so this property
  // is silently dropped before `useQuery(askMoo.customSkills())` ever sees
  // it — it does NOT stop React Query 4's default `retry: 3` (1s + 2s + 4s
  // backoff), which is what makes the My Skills page spin for ~7 seconds
  // before showing an error. The real fix has to set `retry: false` in the
  // object passed to `useQuery` at the call sites: see
  // useCustomSkillsList/useCustomSkill in
  // components/ask-moo/hooks/useCustomSkills.ts (OMNI-773).
  customSkills: () => ({
    queryKey: [{}],
    queryFn: () =>
      getCustomSkills()
        .then(getData)
        .then((response) => response.skills),
    retry: false,
  }),

  customSkill: (skillId: string) => ({
    queryKey: [{ skillId }],
    queryFn: () => getCustomSkill(skillId).then(getData),
    retry: false,
  }),

  activeAsyncRuns: () => ({
    queryKey: [{}],
    // Return the whole response (not just `.active`) so the poller can read
    // `enabled` and stop polling when the feature is off.
    queryFn: () => getActiveAsyncTasks().then(getData),
  }),

  agentTools: (agentId: string) => ({
    queryKey: [{ agentId }],
    queryFn: () =>
      getAgentTools(agentId)
        .then(getData)
        .then((response) => response.tools),
  }),

  starters: (
    agentId: string,
    params?: {
      view?: string;
      subTab?: string;
      entityType?: string;
      entityId?: string;
      entityName?: string;
    }
  ) => ({
    queryKey: [{ agentId, params }],
    queryFn: () =>
      getAgentStarters(agentId, { params })
        .then(getData)
        .catch((err) => {
          if (err?.response?.status === 404) {
            return { starters: [] } as StartersResponse;
          }
          throw err;
        }),
  }),

  integrationCatalog: () => ({
    queryKey: [{}],
    queryFn: () => getIntegrationCatalog().then(getData),
  }),

  integrations: (params?: { status?: string }) => ({
    queryKey: [{ params }],
    queryFn: () =>
      getIntegrations({ params })
        .then(getData)
        .then((r) => r.integrations),
  }),

  integration: (integrationId: string) => ({
    queryKey: [{ integrationId }],
    queryFn: () => getIntegration(integrationId).then(getData),
  }),

  integrationHealth: (integrationId: string) => ({
    queryKey: [{ integrationId }],
    queryFn: () => checkIntegrationHealth(integrationId).then(getData),
  }),

  userConnections: () => ({
    queryKey: [{}],
    queryFn: () =>
      getUserConnections()
        .then(getData)
        .then((r) => r.connections),
  }),

  kbRegistry: () => ({
    queryKey: [{}],
    queryFn: () => getKBRegistry().then(getData),
  }),

  kbTrees: (productLine: string, product: string) => ({
    queryKey: [{ productLine, product }],
    queryFn: () => getKBTrees(productLine, product).then(getData),
    // WARNING: the `retry: false` below is INERT — do not rely on it, and
    // do not "clean it up" by assuming it's redundant with a fix elsewhere.
    // query-key-factory's `createQueryKeys` only forwards
    // `queryKey`/`queryFn`/`contextQueries` from whatever object a factory
    // function returns; any other property (this one included) is silently
    // dropped before `askMoo.kbTrees(...)` ever returns, so it never reaches
    // `useQuery`/`useQueries`. This is the exact same shape as
    // customSkills/customSkill's `retry: false` above (OMNI-773) — which,
    // for the identical reason, has never actually worked either.
    //
    // The REAL fix for the "KB tree fetches that 404 get retried 3x" bug
    // (OMNI-824) lives at the three call sites that spread this object into
    // a literal and add their OWN `retry: false` there, where it actually
    // survives:
    //   - useKBTrees in components/ask-moo/hooks/useKnowledgeBase.ts
    //   - MooStudioKnowledgeBaseView.tsx's useQueries call
    //   - MooStudioAgentDetailsView.tsx's useQueries call
    // Kept here anyway (rather than deleted) only so this entry's shape
    // keeps documenting intent next to its equally-inert customSkills/
    // customSkill neighbors above — if you're fixing a retry bug, go to the
    // three files above, not here.
    retry: false,
  }),

  kbDocTypes: () => ({
    queryKey: [{}],
    queryFn: () => getKBDocTypes().then(getData),
  }),

  kbGroups: () => ({
    queryKey: [{}],
    queryFn: () =>
      getKBGroups()
        .then(getData)
        .then((response) => response.groups),
  }),

  agentKnowledge: (agentId: string) => ({
    queryKey: [{ agentId }],
    queryFn: () => getAgentKnowledge(agentId).then(getData),
  }),

  agentAccess: (agentId: string) => ({
    queryKey: [{ agentId }],
    queryFn: () =>
      getAgentAccess(agentId)
        .then(getData)
        .then((response) => response.access),
  }),

  // ── Custom-agent versioning / datasets / evaluations ─────────────────
  agentVersions: (agentId: string) => ({
    queryKey: [{ agentId }],
    queryFn: () =>
      getAgentVersions(agentId)
        .then(getData)
        .then((response) => response.versions),
  }),

  datasets: (agentId: string) => ({
    queryKey: [{ agentId }],
    queryFn: () =>
      getDatasets(agentId)
        .then(getData)
        .then((response) => response.datasets),
  }),

  datasetVersions: (agentId: string, datasetId: string) => ({
    queryKey: [{ agentId, datasetId }],
    queryFn: () =>
      getDatasetVersions(agentId, datasetId)
        .then(getData)
        .then((response) => response.versions),
  }),

  datasetItems: (agentId: string, datasetId: string, versionId: string) => ({
    queryKey: [{ agentId, datasetId, versionId }],
    queryFn: () =>
      getDatasetItems(agentId, datasetId, versionId)
        .then(getData)
        .then((response) => response.items),
  }),

  evaluations: (agentId: string, datasetId?: string) => ({
    queryKey: [{ agentId, datasetId: datasetId ?? null }],
    queryFn: () =>
      (datasetId
        ? listEvaluations(agentId, { params: { dataset_id: datasetId } })
        : listEvaluations(agentId)
      )
        .then(getData)
        .then((response) => response.runs),
  }),

  evaluation: (agentId: string, runId: string) => ({
    queryKey: [{ agentId, runId }],
    queryFn: () => getEvaluation(agentId, runId).then(getData),
  }),

  evaluationMetrics: () => ({
    queryKey: [{}],
    queryFn: () =>
      getEvaluationMetrics()
        .then(getData)
        .then((response) => response.metrics),
  }),

  userPreferences: () => ({
    queryKey: [{}],
    queryFn: () => getUserPreferences().then(getData),
  }),

  userMemory: () => ({
    queryKey: [{}],
    queryFn: () => getUserMemory().then(getData),
  }),

  userSearch: (query: string) => ({
    queryKey: [{ query }],
    queryFn: () =>
      getUsers({ params: { search: query, page: 0, size: 10 } })
        .then(getData)
        .then((response) =>
          (response.data || []).map(
            (u: {
              id: string;
              firstName?: string;
              lastName?: string;
              email: string;
            }) => ({
              id: u.id,
              name:
                [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email,
              email: u.email,
            })
          )
        ),
  }),

  /**
   * Users in the caller's account, for the page-dwell audience picker
   * (OMNI-757). Unlike `userSearch` this loads a browsable page with no
   * minimum query length — the picker has to let someone add colleagues they
   * cannot spell, not just confirm ones they can.
   */
  accountUsers: (search: string) => ({
    queryKey: [{ search }],
    queryFn: () =>
      getUsers({ params: { search, page: 0, size: 100 } })
        .then(getData)
        .then((response) =>
          (response.data || []).map(
            (u: {
              id: string;
              firstName?: string;
              lastName?: string;
              email: string;
            }) => ({
              id: u.id,
              name:
                [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email,
              email: u.email,
            })
          )
        ),
  }),

  mooTasks: (params?: Record<string, unknown>) => ({
    queryKey: [{ params }],
    queryFn: () => listAllMooTasks(params),
  }),

  mooTask: (taskId: string) => ({
    queryKey: [{ taskId }],
    queryFn: () => getMooTask(taskId).then(getData),
  }),

  mooTaskRuns: (taskId: string, params?: Record<string, unknown>) => ({
    queryKey: [{ taskId, params }],
    queryFn: () => listTaskRunsForTask(taskId, { params }).then(getData),
  }),

  allTaskRuns: (params?: Record<string, unknown>) => ({
    queryKey: [{ params }],
    queryFn: () => listAllTaskRuns({ params }).then(getData),
  }),

  taskRunBackgroundProgress: (taskId: string, runId: string) => ({
    queryKey: [{ taskId, runId }],
    queryFn: () => getTaskRunBackgroundProgress(taskId, runId).then(getData),
  }),

  triggerCatalog: () => ({
    queryKey: ['list'],
    queryFn: () => listTriggerCatalog().then(getData),
  }),

  outputCatalog: () => ({
    queryKey: ['list'],
    queryFn: () => listOutputCatalog().then(getData),
  }),
});
